import { FC } from 'react';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Alert } from '@kontentino/ui';
import { fetchAnalyticsReportPublicPreview } from 'app/modules/analytics/modules/reports/utils/resolveAnalyticsReportPublicPreviewAuth';
import { queryKey } from 'constants/queryKey';
import { ANALYTICS_EXPORT_MOCK_PREVIEW } from './analyticsExportMockPreview';
import AnalyticsExportPresentation from './AnalyticsExportPresentation';
import AnalyticsExportPublicPage, {
  type AnalyticsExportPublicMode,
} from './AnalyticsExportPublicPage';

const AnalyticsExportFailedMessage: FC = () => {
  const { t } = useTranslation();

  return (
    <div className="tw-p-8">
      <Alert variant="danger">
        {t('analyticsReports.publicPreview.failedToBuild.message')}
        <br />
        {t('analyticsReports.publicPreview.failedToBuild.retryMessage')}
      </Alert>
    </div>
  );
};

const AnalyticsExportPublicRoute: FC = () => {
  const { id } = useParams();
  const [params] = useQueryParams({
    token: StringParam,
    accountId: NumberParam,
    mode: StringParam,
    mock: StringParam,
    slide: NumberParam,
  });
  const isMocked = process.env.NODE_ENV === 'development' && !!params.mock;
  const tokenFromUrl = params.token;
  const accountId = params.accountId ?? undefined;
  const mode = params.mode as AnalyticsExportPublicMode | null | undefined;
  const reportId = Number(id);
  const isFiniteReportId = Number.isFinite(reportId);
  const { isLoading, data, isError } = useQuery(
    queryKey.analyticsExportPreview(reportId, tokenFromUrl, accountId),
    () =>
      fetchAnalyticsReportPublicPreview(
        reportId,
        tokenFromUrl,
        accountId,
        true,
      ),
    {
      enabled: !isMocked && isFiniteReportId,
      retry: false,
    },
  );

  if (isMocked) {
    return mode === 'present' ? (
      <AnalyticsExportPresentation
        preview={ANALYTICS_EXPORT_MOCK_PREVIEW}
        initialSlide={params.slide}
      />
    ) : (
      <AnalyticsExportPublicPage
        preview={ANALYTICS_EXPORT_MOCK_PREVIEW}
        mode={mode}
      />
    );
  }

  if (!isFiniteReportId) {
    return <AnalyticsExportFailedMessage />;
  }

  if (isLoading) {
    return (
      <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-py-16">
        <ActivityIndicator />
      </div>
    );
  }

  if (isError || !data) {
    return <AnalyticsExportFailedMessage />;
  }

  return mode === 'present' ? (
    <AnalyticsExportPresentation preview={data} initialSlide={params.slide} />
  ) : (
    <AnalyticsExportPublicPage preview={data} mode={mode} />
  );
};

export default AnalyticsExportPublicRoute;
